// A copy of the map as the glass shows it (R1623, R1626).
//
// Photo mode saves the map as a PNG, and the REPORT sheet takes a
// screenshot when it opens. Both copy the map canvas, and the copy must
// work on both backends (R847). A WebGL2 canvas clears its drawing buffer
// after the browser shows it, and a WebGPU canvas hands its current
// texture back at the end of the task. Neither keeps the picture for a
// copy taken later, so the copy runs in the task of the next frame, right
// after the frame is submitted (`Loop.afterNextFrame`). A 2D `drawImage`
// of the canvas there reads the picture that frame drew, in both backends.
//
// The copy is a canvas of the size of the drawing buffer: the resolution
// of the screen. It holds no DOM: the chrome is not in it.

import { PLAYER_REPORT_SCREENSHOT_MAX_BYTES } from '../gen/balance';
import { REPORT_SHEET } from '../gen/presentation';
import { deviceRatio } from '../render/dpr';

/** What the copy needs of the engine. */
export interface CaptureSource {
  readonly canvas: HTMLCanvasElement;
  readonly loop: { readonly isRunning: boolean; afterNextFrame(fn: () => void): void };
}

/**
 * A copy of the next frame of the map, or null where no frame comes (a
 * hidden page stops the loop) or the browser gives no 2D context.
 */
export function captureMap(src: CaptureSource): Promise<HTMLCanvasElement | null> {
  if (!src.loop.isRunning) return Promise.resolve(null);
  return new Promise((resolve) => {
    src.loop.afterNextFrame(() => {
      const w = src.canvas.width, h = src.canvas.height;
      const out = document.createElement('canvas');
      out.width = w;
      out.height = h;
      const c = out.getContext('2d');
      if (!c || w === 0 || h === 0) { resolve(null); return; }
      c.drawImage(src.canvas, 0, 0, w, h);
      resolve(out);
    });
  });
}

/** The canvas as a PNG blob, or null where the browser gives none. */
export function pngOf(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/png');
    } catch {
      resolve(null);
    }
  });
}

/** A copy of `src` whose long side is at most `edge` px, or `src` itself
 *  when it is already that small. */
function scaledTo(src: HTMLCanvasElement, edge: number): HTMLCanvasElement {
  const long = Math.max(src.width, src.height);
  if (long <= edge) return src;
  const k = edge / long;
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(src.width * k));
  out.height = Math.max(1, Math.round(src.height * k));
  out.getContext('2d')?.drawImage(src, 0, 0, out.width, out.height);
  return out;
}

/**
 * The screenshot of the REPORT sheet (R1626): the map now, as a PNG under
 * `[meta.player_reports] screenshot_max_bytes`, and a thumbnail address.
 * The PNG starts at a long side of `shotMaxEdgePx` and shrinks by
 * `shotStep` until it fits. Under `shotMinEdgePx` the report goes without
 * a screenshot, and the answer is null.
 */
export async function reportShot(src: CaptureSource): Promise<{ png: Uint8Array; thumb: string } | null> {
  const map = await captureMap(src);
  if (!map) return null;
  const dpr = deviceRatio();
  const thumb = scaledTo(map, Math.ceil(Math.max(REPORT_SHEET.thumbWPx, REPORT_SHEET.thumbHPx) * dpr))
    .toDataURL('image/png');
  for (let edge: number = REPORT_SHEET.shotMaxEdgePx; edge >= REPORT_SHEET.shotMinEdgePx;
    edge = Math.floor(edge * REPORT_SHEET.shotStep)) {
    const blob = await pngOf(scaledTo(map, edge));
    if (!blob) return null;
    if (blob.size <= PLAYER_REPORT_SCREENSHOT_MAX_BYTES) {
      return { png: new Uint8Array(await blob.arrayBuffer()), thumb };
    }
  }
  return null;
}
